import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/ui/footer";
import { Navbar } from "@/components/ui/navbar";
import { OrganizerModal } from "@/components/ui/organizerModal";
import {
  ArrowRight,
  Check,
  X,
  MapPin,
  FileText,
  Mountain,
  Truck,
  ChevronDown,
  ChevronUp,
  Download,
  Map,
  Route as RouteIcon,
  ExternalLink,
  Pencil,
  Zap,
  Star,
} from "lucide-react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://wayra.cc";

export const Route = createFileRoute("/organizers")({
  component: OrganizersPage,
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/organizers` }],
  }),
});

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: "Do I have to move my registration to Wayra?",
    answer:
      "No. Claiming your event just gives you a better page—you keep using BikeReg/Active for registration. We link to it prominently. Native hosting (where we handle registration) is completely optional.",
  },
  {
    question: "What if my event is already on BikeReg?",
    answer:
      "Perfect. It's probably already indexed on Wayra as a basic listing. Claiming it lets you upgrade the page with route files, photos, and logistics—then we link riders to your BikeReg registration.",
  },
  {
    question: "How much does claiming cost?",
    answer:
      "Claiming is free. Forever. Native hosting (if you choose it later) is 5% + payment processing. But claiming alone—where you get the better page and still use BikeReg—costs nothing.",
  },
  {
    question: "Why should I trust you with my event data?",
    answer:
      "Fair question. We're gravel riders who got tired of hunting for events. We built this for us, and we're sharing it with you. Your event data stays yours. We're just helping riders discover it.",
  },
];

function FAQAccordion({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div
          key={index}
          className="border border-slate-200 rounded-xl overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-5 text-left flex items-center justify-between bg-white hover:bg-slate-50 transition-colors"
          >
            <span className="font-semibold text-slate-900">
              {item.question}
            </span>
            {openIndex === index ? (
              <ChevronUp className="w-5 h-5 text-slate-500 shrink-0" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-500 shrink-0" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-6 pb-5 bg-white">
              <p className="text-slate-600">{item.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function OrganizersPage() {
  const [email, setEmail] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Handle form submission
    console.log("Submitted email:", email);
  };

  const handleClaimEvent = () => {
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero Section */}
      <section
        className="text-white"
        style={{
          background:
            "linear-gradient(160deg, #1a1a1a 0%, #2d1f14 50%, #3d2112 100%)",
        }}
      >
        <div className="container mx-auto px-6 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-medium mb-6">
              For Event Organizers
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Riders Are Searching For Your Event Right Now
            </h1>
            <p className="text-lg sm:text-xl text-indigo-100 mb-8 max-w-2xl">
              Help them find it—and show up prepared with all the info they
              need.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-base font-medium"
                onClick={handleClaimEvent}
                data-mp-element-name="claim-event-cta"
                data-mp-cta-location="organizers-hero"
                data-mp-cta-variant="primary"
              >
                Claim Your Event
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Your Event Is Already On Wayra Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Your Event Is Already On Wayra
            </h2>
            <p className="text-lg text-slate-600">
              Riders can already find it. Claiming it helps them choose to sign
              up.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What Riders Experience */}
            <div className="bg-white border-2 border-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">
                What Riders Experience
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Riders have to check BikeReg, Active, RUSA, regional
                    calendars, Facebook just to find events.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    When they find you, the page has basic facts but no
                    context—what's the terrain like? Where do I park?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    They email you asking for GPX files, parking info,
                    lodging—over and over.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    If they do find you, they bounce between tabs trying to get
                    the full picture.
                  </span>
                </li>
              </ul>
            </div>

            {/* With Wayra */}
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">
                With Wayra
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Riders search once. Your event shows up with everyone
                    else's—no matter where it's registered.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    <span className="font-semibold text-slate-700">
                      Upgraded
                    </span>{" "}
                    event pages: course maps, elevation profiles, GPX downloads,
                    parking details, photos.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Stop fielding the same questions. Everything riders need to
                    show up prepared is on the page.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Map-based discovery: riders browsing your region see your
                    event, even if they didn't know about it.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Here's The Deal Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-4">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">🤝</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Here's The Deal: Start Free, Upgrade Anytime
            </h2>
            <p className="text-lg text-slate-600">
              We're indexing gravel events from BikeReg, Active, RUSA, and
              regional calendars. Your event is probably already on Wayra. We're
              not asking you to switch—we're offering you a gift:{" "}
              <span className="font-semibold">a better event page.</span>
            </p>
          </div>

          {/* Pricing Tiers */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12">
            {/* Indexed Event */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                <ExternalLink className="w-6 h-6 text-slate-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Indexed Event
              </h3>
              <p className="text-slate-500 mb-6">Your current status</p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 shrink-0"></div>
                  <span className="text-slate-600">
                    Basic info (date, location, price)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 shrink-0"></div>
                  <span className="text-slate-600">Shows up in searches</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 shrink-0"></div>
                  <span className="text-slate-600">
                    Links to your BikeReg page
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 shrink-0"></div>
                  <span className="text-slate-600">We drive you traffic</span>
                </li>
              </ul>
            </div>

            {/* Claimed Event - Recommended */}
            <div className="bg-orange-50 rounded-2xl p-8 border-2 border-primary relative shadow-lg">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  Recommended
                </span>
              </div>
              <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-6">
                <Pencil className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 text-left">
                Claimed Event
              </h3>
              <p className="text-slate-900 font-medium mb-6 text-left">
                Free Forever
              </p>
              <ul className="space-y-3 text-sm mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-slate-700 font-medium">
                    Everything from indexed +
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-slate-700 font-medium">
                    Upload GPX files & maps
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-slate-700 font-medium">
                    Add photos & descriptions
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-slate-700 font-medium">
                    Parking, lodging, logistics
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-slate-700 font-medium">
                    Still links to your BikeReg
                  </span>
                </li>
              </ul>
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6"
                onClick={handleClaimEvent}
                data-mp-element-name="claim-event-cta"
                data-mp-cta-location="organizers-pricing"
                data-mp-cta-variant="primary"
              >
                Claim Your Event
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Native Hosting */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
              <div className="w-14 h-14 bg-purple-500 rounded-full flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Native Hosting
              </h3>
              <p className="text-slate-500 mb-6">When you're ready</p>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Everything from claimed +
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Host registration on Wayra
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    5% + processing (vs 6% or more)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    Participant management tools
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-slate-600">Keep your relationship</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-slate-500 mt-8 text-sm max-w-2xl mx-auto">
            <span className="font-semibold">Start with claiming.</span> It's
            free, takes 5 minutes, and you keep using BikeReg. You decide later
            if you want to host registration with us.
          </p>
        </div>
      </section>

      {/* See The Difference Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              See The Difference
            </h2>
            <p className="text-lg text-slate-600">
              Here's what an indexed page looks like vs. a claimed page
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Indexed Page */}
            <div>
              <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1 text-xs font-medium text-slate-600 mb-4">
                Indexed Page
              </div>
              <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Flint Hills Gravel 100
                </h3>
                <p className="text-sm text-slate-500 mb-6">
                  June 15, 2025 • Emporia, KS
                </p>

                <div className="space-y-4 text-sm">
                  <div>
                    <span className="text-slate-500">Distance</span>
                    <p className="text-slate-900">100 miles</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Price</span>
                    <p className="text-slate-900">$75</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Registration</span>
                    <a
                      href="#"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      View on BikeReg <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                <p className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-100">
                  Basic info only. No maps, no logistics, no photos.
                </p>
              </div>
            </div>

            {/* Claimed Page */}
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-3 py-1 text-xs font-medium text-primary mb-4">
                Claimed Page
              </div>
              <div className="bg-white border-2 border-primary/30 rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Flint Hills Gravel 100
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  June 15, 2025 • Emporia, KS
                </p>

                <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                  <RouteIcon className="w-4 h-4 text-primary" />
                  <span>Route & Elevation</span>
                </div>

                <div className="bg-slate-100 rounded-xl h-24 flex items-center justify-center text-slate-400 text-sm mb-4">
                  (Course map preview)
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <Download className="w-4 h-4 text-primary" />
                  <span>Downloads</span>
                </div>
                <p className="text-sm text-slate-500 mb-4 pl-6">
                  GPX • Cue Sheet
                </p>

                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>Logistics</span>
                </div>
                <p className="text-sm text-slate-500 mb-6 pl-6">
                  Parking at City Park. Arrive early for packet pickup.
                </p>

                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-white rounded-full"
                  data-mp-element-name="register-bikereg-demo"
                  data-mp-cta-location="organizers-comparison"
                  data-mp-cta-variant="demo"
                >
                  Register on BikeReg
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          <p className="text-center text-slate-600 mt-8 text-sm max-w-2xl mx-auto">
            <span className="font-semibold">
              Same event. Same registration link.
            </span>{" "}
            Just a better page that gives riders everything they need to commit.
          </p>
        </div>
      </section>

      {/* What You Get When You Claim Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What You Get When You Claim
            </h2>
            <p className="text-lg text-slate-600">
              These are the tools riders wish every event had
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {/* Route Files Front & Center */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                Route Files Front & Center
              </h3>
              <p className="text-sm text-slate-600">
                Upload GPX files, course maps, cue sheets. Stop answering
                "where's the GPX?" emails.
              </p>
            </div>

            {/* Map-Based Discovery */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Map className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                Map-Based Discovery
              </h3>
              <p className="text-sm text-slate-600">
                Riders browsing your region see your event on the map. They
                don't need to know your name yet.
              </p>
            </div>

            {/* Elevation Profiles That Matter */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Mountain className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                Elevation Profiles That Matter
              </h3>
              <p className="text-sm text-slate-600">
                Riders want to know what they're signing up for. Show them the
                climbs, the descents, the pain.
              </p>
            </div>

            {/* Logistics = Less Stress */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                Logistics + Less Stress
              </h3>
              <p className="text-sm text-slate-600">
                Parking instructions, lodging tips, what to bring. Put it on the
                page so riders show up prepared.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-12 text-center">
              Questions Organizers Ask
            </h2>
            <FAQAccordion items={faqItems} />
          </div>
        </div>
      </section>

      <Footer />
      <OrganizerModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
